import { useState } from "react";
import { t } from "../i18n";

function formatDuration(seconds) {
  if (!seconds) return "--:--";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function displayName(track, lang) {
  if (lang === 'zh' && track.name_zh) return track.name_zh;
  if (lang === 'en' && track.name_en) return track.name_en;
  // Fallback: clean up the slug-like name
  return track.name
    ? track.name.replace(/-/g, ' ')
    : (lang === 'zh' ? '未知曲目' : 'Unknown Track');
}

export default function TrackDetailsModal({
  track,
  scene,
  lang = 'zh',
  likedTracks,
  favoritedTracks,
  onPlayTrack,
  onAddToPlaylist,
  onToggleLike,
  onToggleFavorite,
  onClose,
  onShowPricing,
}) {
  const isLiked = likedTracks?.has(track.id);
  const isFavorited = favoritedTracks?.has(track.id);
  const [addedToPlaylist, setAddedToPlaylist] = useState(false);
  const [downloading, setDownloading] = useState(false);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-lg w-full max-h-[80vh] flex flex-col animate-fade-in">
        {/* Header with scene backdrop */}
        <div
          className="h-36 bg-cover bg-center relative shrink-0"
          style={{ backgroundImage: `url(${scene.image})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 left-3 w-8 h-8 rounded-full bg-black/40 backdrop-blur flex items-center justify-center text-white hover:bg-black/60 transition"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="absolute bottom-3 left-4 right-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg">{scene.emoji}</span>
              <h2 className="text-xl font-bold text-white">{lang === 'zh' ? scene.name : scene.nameEn}</h2>
            </div>
            <p className="text-white/70 text-sm truncate">{displayName(track, lang)}</p>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Track info grid */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wider">{t(lang, 'trackType')}</label>
              <p className="text-sm font-medium text-gray-800 mt-0.5">
                {track.type === "songs" ? t(lang, 'vocal') : t(lang, 'instrumental')}
              </p>
            </div>
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wider">{t(lang, 'duration')}</label>
              <p className="text-sm font-medium text-gray-800 mt-0.5">{formatDuration(track.duration_s)}</p>
            </div>
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wider">{t(lang, 'category')}</label>
              <p className="text-sm font-medium text-gray-800 mt-0.5 capitalize">{track.category || '—'}</p>
            </div>
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wider">{t(lang, 'trackId')}</label>
              <p className="text-sm font-medium text-gray-800 mt-0.5 font-mono text-xs">{track.id}</p>
            </div>
          </div>

          {/* Tags */}
          {scene.tags && scene.tags.length > 0 && (
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wider mb-2 block">{t(lang, 'tags')}</label>
              <div className="flex flex-wrap gap-1.5">
                {scene.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 bg-indigo-50 text-indigo-600 text-xs rounded-full font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wider mb-2 block">{t(lang, 'description')}</label>
            <p className="text-sm text-gray-600 leading-relaxed">
              {lang === 'zh' ? scene.description : scene.descriptionEn || scene.description}
            </p>
          </div>

          {/* Mood / Use Cases */}
          <div className="grid grid-cols-2 gap-4">
            {scene.mood && scene.mood.length > 0 && (
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider mb-2 block">{t(lang, 'mood')}</label>
                <div className="flex flex-wrap gap-1.5">
                  {scene.mood.map((m) => (
                    <span key={m} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                      {m}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {scene.useCases && scene.useCases.length > 0 && (
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider mb-2 block">{t(lang, 'useCasesShort')}</label>
                <div className="flex flex-wrap gap-1.5">
                  {scene.useCases.map((u) => (
                    <span key={u} className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs rounded">
                      {u}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* License info */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
            <div className="flex items-start gap-2">
              <span className="text-amber-500 shrink-0 mt-0.5">🛡️</span>
              <div>
                <p className="text-sm font-medium text-amber-800">{t(lang, 'licenseInfo')}</p>
                <p className="text-xs text-amber-600 mt-0.5">{t(lang, 'licenseDesc')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="px-5 py-4 bg-gray-50 border-t border-gray-100 flex items-center gap-2 shrink-0">
          {/* Play */}
          <button
            onClick={() => {
              onPlayTrack(track, scene);
              onClose();
            }}
            className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition shadow-sm"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
            {t(lang, 'play')}
          </button>

          {/* Add to playlist */}
          <button
            onClick={() => {
              onAddToPlaylist(track);
              setAddedToPlaylist(true);
              setTimeout(() => setAddedToPlaylist(false), 1500);
            }}
            className={`w-9 h-9 rounded-lg flex items-center justify-center transition ${
              addedToPlaylist
                ? "text-emerald-500 bg-emerald-50 scale-110"
                : "text-gray-400 hover:text-indigo-500 hover:bg-indigo-50"
            }`}
            title={t(lang, 'addToPlaylist')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {addedToPlaylist ? (
                <>
                  <polyline points="20 6 9 17 4 12" />
                </>
              ) : (
                <>
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </>
              )}
            </svg>
          </button>

          {/* Like */}
          <button
            onClick={() => onToggleLike(track.id, track, scene)}
            className={`w-9 h-9 rounded-lg flex items-center justify-center transition ${
              isLiked
                ? "text-red-500 bg-red-50"
                : "text-gray-400 hover:text-red-400 hover:bg-gray-100"
            }`}
            title={isLiked ? t(lang, 'unlike') : t(lang, 'like')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill={isLiked ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
          </button>

          {/* Favorite */}
          <button
            onClick={() => onToggleFavorite(track.id, track, scene)}
            className={`w-9 h-9 rounded-lg flex items-center justify-center transition ${
              isFavorited
                ? "text-amber-500 bg-amber-50"
                : "text-gray-400 hover:text-amber-400 hover:bg-gray-100"
            }`}
            title={isFavorited ? t(lang, 'unfavorite') : t(lang, 'favorite')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill={isFavorited ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          </button>

          <div className="flex-1" />

          {/* Download */}
          <button
            onClick={async () => {
              const token = localStorage.getItem("fm_token");
              if (!token) {
                window.dispatchEvent(new CustomEvent('open-login-modal'));
                return;
              }
              setDownloading(true);
              try {
                const res = await fetch(`/api/tracks/${track.id}/download`, {
                  method: "POST",
                  headers: { Authorization: `Bearer ${token}` },
                });
                if (res.status === 403) {
                  onShowPricing?.();
                  return;
                }
                if (!res.ok) throw new Error(t(lang, 'downloadFailed'));
                const data = await res.json();
                const a = document.createElement("a");
                a.href = data.url || data.download_url;
                a.download = track.name || `track_${track.id}`;
                a.click();
              } catch (e) {
                console.error("Download error:", e);
              } finally {
                setDownloading(false);
              }
            }}
            disabled={downloading}
            className="flex items-center gap-1.5 px-4 py-2 border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
            title={t(lang, 'download')}
          >
            {downloading ? (
              <svg className="animate-spin" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="10" strokeDasharray="31.4" strokeDashoffset="10" />
              </svg>
            ) : (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            )}
            {t(lang, 'download')}
          </button>
        </div>
      </div>
    </div>
  );
}
